// src/components/Tour/TourManager.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTour } from '../../contexts/TourContext';
import { tourConfig } from '../../config/tourConfig';

const TourManager = () => {
  const location = useLocation();
  const { startTour, tourState } = useTour();
  const isCheckingRef = useRef(false);

  useEffect(() => {
    // Don't interrupt an active tour
    if (tourState.isActive) return;
    
    // Don't run multiple checks simultaneously
    if (isCheckingRef.current) return;

    const currentPath = location.pathname;

    // Check global toggle
    if (!tourConfig.globallyEnabled) return;

    // Check if route has a tour
    const tour = tourConfig.getTourForRoute(currentPath);
    if (!tour || !tour.enabled) return;

    // Check if tour already seen
    if (localStorage.getItem(tour.storageKey)) return;

    isCheckingRef.current = true;

    // Function to attempt showing tour
    const attemptTour = (attempt = 1, maxAttempts = 5) => {
      const allElementsExist = tour.steps.every(
        step => document.querySelector(step.targetSelector)
      );

      if (allElementsExist) {
        startTour(tour.steps);
        tourConfig.markTourSeen(currentPath);
        isCheckingRef.current = false;
      } else if (attempt < maxAttempts) {
        // Retry with increasing delay
        const retryDelay = 1000 * attempt;
        console.log(`Tour elements not ready, retrying in ${retryDelay}ms (attempt ${attempt}/${maxAttempts})`);
        
        setTimeout(() => {
          attemptTour(attempt + 1, maxAttempts);
        }, retryDelay);
      } else {
        // Max attempts reached, mark as seen to prevent infinite retries
        console.warn(`Tour skipped for ${currentPath}: Elements not found after ${maxAttempts} attempts`);
        tourConfig.markTourSeen(currentPath);
        isCheckingRef.current = false;
      }
    };

    // Start checking after delay
    const delay = tour.delay || tourConfig.defaultDelay;
    const timer = setTimeout(() => {
      attemptTour();
    }, delay);

    return () => {
      clearTimeout(timer);
      isCheckingRef.current = false;
    };
  }, [location.pathname, tourState.isActive]);

  // Dev helper: Press Ctrl+Shift+T to reset all tours
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handler = (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'T') {
          tourConfig.resetAllTours();
          console.log('🔄 All tours reset! Refresh the page to see them again.');
          alert('All tours have been reset! Refresh the page to see the tours again.');
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, []);

  return null; // This component doesn't render anything
};

export default TourManager;